/* eslint-disable max-lines */
// deps
import React, { ReactElement, useCallback, useContext, useMemo, useState } from "react";

// internal-deps
import { ErrorBoundary, isEmpty, useEffectOnce } from "@swiggy-private/core-ui";
import { NativeBridge } from "@swiggy-private/native-bridge";
import {
    recordNREvent,
    CheckoutActionTypes,
    CheckoutState,
    useInit,
    CheckoutRoute,
    SwgyHeadersKeys,
    AnalyticsContext,
    recordWebviewRefreshState,
    restoreWebviewRefreshState,
    GlobalHeaderState,
    NEWRELIC_TABLES,
    getBridgeSdk,
    TurboPayConfig,
    TurboPayPlaceOrderData,
} from "@swiggy-private/checkout-core";
import { MARKETPLACE_BUSINESS_LINE, PAYLATER_TYPES } from "@swiggy-private/payment-types";
import { getNRErrorInfo } from "@swiggy-private/js-utils";

// components
import { ContextProvider } from "@payment-client/components/context-provider";
import { PaymentRouter } from "@payment-client/components/payment-router/PaymentRouter";
import { PaymentModesRouter } from "@payment-client/components/payment-modes-router";
import TurboPaymentPage from "@payment-client/components/turbo-payment/TurboPaymentPage";

// helpers
import { isPaymentModesState } from "@payment-client/helpers/Misc";
import { PaymentModesErrorScreen } from "@payment-client/components/payment-modes/components/error-screen";
import { cacheOriginalBusinessLine } from "@payment-client/helpers/SwiggyInstrumentsHelper";

// analytics
import { paymentScreenViewEvent } from "@payment-client/analytics/events/Payment";
import { recordPaymentPercievedTime } from "@payment-client/helpers/Metrics";

export enum PAYMENT_INITIAL_STATE {
    DIRECT_PLACE_ORDER = "DIRECT_PLACE_ORDER",
    CONFIRM_ORDER = "CONFIRM_ORDER",
    WALLET_MONEY_RECEIVED = "WALLET_MONEY_RECEIVED",
    ADD_NEW_CARD = "ADD_NEW_CARD",
    MANAGE_CARD = "MANAGE_CARD",
    SECURE_CARD = "SECURE_CARD",
    CONFIRM_TRANSACTION = "CONFIRM_TRANSACTION",
    PENNY_TXN_STATUS_POLLER = "PENNY_TXN_STATUS_POLLER",
    LINK_WALLET_WITH_NUMBER = "LINK_WALLET_WITH_NUMBER",
    ADD_MONEY_AND_DELINK_WALLET = "ADD_MONEY_AND_DELINK_WALLET",
    ADD_SWIGGY_MONEY_GENERIC = "ADD_SWIGGY_MONEY_GENERIC",
    PAYLATER_LINKING_CONFIRMATION = "LINK_LAZYPAY_CONFIRMATION",
    TURBO_PAY = "TURBO_PAY",
    TURBO_PLACE_ORDER = "TURBO_PLACE_ORDER",
    PAYLATER_SIMPL_LINKING_FAILED = "PAYLATER_SIMPL_LINKING_FAILED",
    PAYLATER_SIMPL_LINKING_SUCCESS_DONT_PROCEED_WITH_ORDER = "PAYLATER_SIMPL_LINKING_SUCCESS_DONT_PROCEED_WITH_ORDER",
    PAYLATER_SIMPL_LINKING_SUCCESS_PROCEED_WITH_ORDER = "PAYLATER_SIMPL_LINKING_SUCCESS_PROCEED_WITH_ORDER",
    DINEOUT_SUCCESS_SCREEN = "DINEOUT", //for direclty landing on the dineout success screen
    EMI_SELECTION = "EMI_SELECTION",
}

export interface PaymentMainProps {
    config: {
        apiHost: string;
        paymentHost: string;
        analyticsHost: string;
        paymentReturnUrl?: string;
        addMoneyReturnUrl?: string;
        addSwiggyMoneyReturnUrl?: string;
        cachePaymentData?: boolean;
        newrelicEndpoint?: string;
        newrelicKey?: string;
        environment?: string;
        enableCardRateLimit?: boolean;
        enablePublicApi?: boolean;
        simplPostLinkingRedirectUrl: string;
        enableWebviewRefreshCapability?: boolean;
        enableConfirmOrderDifferentialOrderIdAlert?: boolean;
        isJuspayV3Enabled?: boolean;
    };
    headers: { [key in Partial<SwgyHeadersKeys>]: string };
    initialState?: {
        state?: PAYMENT_INITIAL_STATE;
        routeName?: CheckoutRoute;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        routeData?: any;
    };
    nativeBridgeSdk?: NativeBridge;
    turboPayConfig?: TurboPayConfig;
    turboPayPlaceOrderData?: TurboPayPlaceOrderData;
    externalRoutes?: React.JSX.Element[];
}

export function PaymentMain(props: PaymentMainProps): ReactElement {
    const {
        config,
        headers,
        initialState: paymentState = {},
        turboPayConfig,
        turboPayPlaceOrderData,
        externalRoutes = [],
    } = props;
    const { screenViewEvent } = useContext(AnalyticsContext);

    const [initialRoute] = useState(() => {
        // Checking if user webview refresh happened and redirecting user to its last interactive state
        const refreshData = restoreWebviewRefreshState(config?.enableWebviewRefreshCapability);
        try {
            // TODO: remove this temporary debug log once debugging is concluded for IM duplicate order issue
            recordNREvent({
                eventType: NEWRELIC_TABLES.WEBVIEW_REFRESH_DEBUG,
                name: "restoreWebviewRefreshStateInitialRoute",
                isUserLastStateNonEmpty: String(!isEmpty(refreshData)),
            });
        } catch {
            /* No-op */
        }
        if (!isEmpty(refreshData)) {
            return {
                name: CheckoutRoute.PAYMENTS,
            };
        }
        switch (paymentState.state) {
            case PAYMENT_INITIAL_STATE.CONFIRM_ORDER:
                return { name: CheckoutRoute.CONFIRM_ORDER };
            case PAYMENT_INITIAL_STATE.DINEOUT_SUCCESS_SCREEN:
                return {
                    name: CheckoutRoute.DINEOUT_PAYMENT_SUCCESS,
                    routeData: paymentState?.routeData,
                    data: paymentState.routeData,
                };

            case PAYMENT_INITIAL_STATE.DIRECT_PLACE_ORDER:
                return { name: CheckoutRoute.DIRECT_PLACE_ORDER };

            case PAYMENT_INITIAL_STATE.ADD_NEW_CARD:
                return {
                    name: CheckoutRoute.PAYMENT_MODES_ADD_NEW_CARD,
                    data: paymentState?.routeData,
                };

            case PAYMENT_INITIAL_STATE.MANAGE_CARD:
                return { name: CheckoutRoute.MANAGE_CARD };

            case PAYMENT_INITIAL_STATE.SECURE_CARD:
                return { name: CheckoutRoute.SECURE_CARD };

            case PAYMENT_INITIAL_STATE.CONFIRM_TRANSACTION:
                return { name: CheckoutRoute.CONFIRM_TRANSACTION };

            case PAYMENT_INITIAL_STATE.PENNY_TXN_STATUS_POLLER:
                return { name: CheckoutRoute.PENNY_TXN_STATUS_POLLER };

            case PAYMENT_INITIAL_STATE.LINK_WALLET_WITH_NUMBER:
                return {
                    name: CheckoutRoute.PAYMENT_MODES_LINK_WALLET_WITH_NUMBER,
                    data: paymentState.routeData,
                };

            case PAYMENT_INITIAL_STATE.ADD_MONEY_AND_DELINK_WALLET:
                return {
                    name: CheckoutRoute.WALLET_DETAILS,
                    data: paymentState.routeData,
                };

            case PAYMENT_INITIAL_STATE.ADD_SWIGGY_MONEY_GENERIC:
                return { name: CheckoutRoute.ADD_SWIGGY_MONEY };

            case PAYMENT_INITIAL_STATE.PAYLATER_LINKING_CONFIRMATION:
                return { name: CheckoutRoute.PAYLATER_LINKING_CONFIRMATION };

            case PAYMENT_INITIAL_STATE.TURBO_PAY:
                return { name: CheckoutRoute.TURBO_PAY };

            case PAYMENT_INITIAL_STATE.TURBO_PLACE_ORDER:
                return {
                    name: CheckoutRoute.TURBO_PLACE_ORDER,
                    data: paymentState?.routeData,
                };
            case PAYMENT_INITIAL_STATE.EMI_SELECTION: {
                return {
                    name: CheckoutRoute.EMI_SELECTION,
                };
            }
        }

        if (paymentState.routeName) {
            return { name: paymentState.routeName, data: paymentState.routeData };
        }

        return { name: CheckoutRoute.PAYMENTS };
    });

    const [initialState] = useState<CheckoutState>((): CheckoutState => {
        // Checking if user webview refresh happened and redirecting user to its last interactive state
        const restoredWebviewRefreshState = restoreWebviewRefreshState(config?.enableWebviewRefreshCapability);
        try {
            // TODO: remove this temporary debug log once debugging is concluded for IM duplicate order issue
            recordNREvent({
                eventType: NEWRELIC_TABLES.WEBVIEW_REFRESH_DEBUG,
                name: "restoreWebviewRefreshStateInitialState",
                isUserLastStateNonEmpty: String(!isEmpty(restoredWebviewRefreshState)),
            });
        } catch {
            /* No-op */
        }
        if (!isEmpty(restoredWebviewRefreshState)) {
            return restoredWebviewRefreshState;
        }

        switch (paymentState.state) {
            case PAYMENT_INITIAL_STATE.WALLET_MONEY_RECEIVED:
                return { type: CheckoutActionTypes.ADD_MONEY_CONFIRM };

            case PAYMENT_INITIAL_STATE.DINEOUT_SUCCESS_SCREEN:
                return { type: CheckoutActionTypes.DINEOUT };

            case PAYMENT_INITIAL_STATE.DIRECT_PLACE_ORDER:
                return { type: CheckoutActionTypes.DIRECT_PLACE_ORDER };

            case PAYMENT_INITIAL_STATE.PAYLATER_SIMPL_LINKING_FAILED:
                return {
                    type: CheckoutActionTypes.PAYLATER_SIMPL_LINKING_FAILED,
                    payload: {
                        error: true,
                        paymentCode: PAYLATER_TYPES.SIMPL,
                    },
                };
            case PAYMENT_INITIAL_STATE.PAYLATER_SIMPL_LINKING_SUCCESS_DONT_PROCEED_WITH_ORDER:
                return {
                    type: CheckoutActionTypes.PAYLATER_SIMPL_LINKING_FAILED,
                    payload: { paymentCode: PAYLATER_TYPES.SIMPL },
                };

            case PAYMENT_INITIAL_STATE.PAYLATER_SIMPL_LINKING_SUCCESS_PROCEED_WITH_ORDER:
                return {
                    type: CheckoutActionTypes.PAYLATER_SIMPL_LINKING_SUCCESS_PROCEED_WITH_ORDER,
                    payload: {
                        paymentCode: PAYLATER_TYPES.SIMPL,
                    },
                };
            case PAYMENT_INITIAL_STATE.ADD_NEW_CARD:
                return { type: CheckoutActionTypes.ADD_NEW_CARD };

            case PAYMENT_INITIAL_STATE.MANAGE_CARD:
                return { type: CheckoutActionTypes.MANAGE_CARD };

            case PAYMENT_INITIAL_STATE.SECURE_CARD:
                return { type: CheckoutActionTypes.SECURE_CARD };

            case PAYMENT_INITIAL_STATE.LINK_WALLET_WITH_NUMBER:
                return { type: CheckoutActionTypes.LINK_WALLET_WITH_NUMBER };

            case PAYMENT_INITIAL_STATE.ADD_MONEY_AND_DELINK_WALLET:
                return { type: CheckoutActionTypes.ADD_MONEY_AND_DELINK_WALLET };

            case PAYMENT_INITIAL_STATE.ADD_SWIGGY_MONEY_GENERIC:
                return { type: CheckoutActionTypes.SHOW_SWIGGYMONEY_PAYMENTS };

            case PAYMENT_INITIAL_STATE.PAYLATER_LINKING_CONFIRMATION:
                return { type: CheckoutActionTypes.PAYLATER_LINKING_CONFIRMATION };

            case PAYMENT_INITIAL_STATE.CONFIRM_ORDER: {
                return {
                    type: CheckoutActionTypes.CONFIRM_ORDER,
                    payload: null,
                };
            }

            case PAYMENT_INITIAL_STATE.TURBO_PAY:
                return { type: CheckoutActionTypes.TURBO_PAY };

            case PAYMENT_INITIAL_STATE.TURBO_PLACE_ORDER:
                return { type: CheckoutActionTypes.TURBO_PLACE_ORDER };
            case PAYMENT_INITIAL_STATE.EMI_SELECTION:
                return { type: CheckoutActionTypes.EMI_SELECTION };
        }
        return { type: CheckoutActionTypes.SHOW_PAYMENTS };
    });
    const loadPaymentModeRoute = useMemo(() => isPaymentModesState(paymentState.state), [paymentState.state]);

    useInit({
        headers,
        nbSdk: props.nativeBridgeSdk,
        newrelicEndpoint: config.newrelicEndpoint,
        newrelicKey: config.newrelicKey,
        environment: config.environment,
    });

    useEffectOnce(() => {
        // This is to record the percieved time taken for the payment screen to load
        recordPaymentPercievedTime();

        getBridgeSdk()
            .setScreenCaptureState({ screenCapturingDisabled: false })
            .catch(() => void 0);

        paymentScreenViewEvent(screenViewEvent);
        recordNREvent({
            eventType: NEWRELIC_TABLES.PAYMENT_WEB_VIEW,
        });

        // clients need to send this flag as true to allow webview refresh going forward
        recordWebviewRefreshState(config?.enableWebviewRefreshCapability);

        if (paymentState.state === PAYMENT_INITIAL_STATE.ADD_SWIGGY_MONEY_GENERIC) {
            cacheOriginalBusinessLine();
            GlobalHeaderState.updateHeaders({
                paymentamount: "",
                marketplacebusinessline: MARKETPLACE_BUSINESS_LINE.SWIGGY_MONEY,
            });
        }
    });

    const customErrorScreenForPaymentModes = useMemo(
        () =>
            loadPaymentModeRoute && paymentState.state ? (
                <PaymentModesErrorScreen state={paymentState.state} routeData={paymentState.routeData} />
            ) : undefined,
        [paymentState, loadPaymentModeRoute],
    );

    const isTurboPay = useMemo(() => initialState.type === CheckoutActionTypes.TURBO_PAY, [initialState.type]);

    const renderRouter = useMemo(
        () =>
            loadPaymentModeRoute ? (
                <PaymentModesRouter initialRoute={initialRoute} />
            ) : (
                <PaymentRouter initialRoute={initialRoute} externalRoutes={externalRoutes} />
            ),
        [initialRoute, loadPaymentModeRoute, externalRoutes],
    );

    const onError = useCallback((error?: Error, errorInfo?: React.ErrorInfo) => {
        const errorEvents = getNRErrorInfo(error, errorInfo);

        recordNREvent({
            eventType: NEWRELIC_TABLES.CHECKOUT_WEB_ERROR_BOUNDARY,
            ...errorEvents,
        });
    }, []);

    return (
        <ErrorBoundary errorContent={customErrorScreenForPaymentModes} errorFallback={onError}>
            <ContextProvider
                config={config}
                turboPayConfig={turboPayConfig}
                turboPayPlaceOrderData={turboPayPlaceOrderData}
                initialState={initialState}
                headers={headers}
            >
                <>
                    {/* When rendering turbo widget we dont need Router since its used as a widget*/}
                    {isTurboPay ? <TurboPaymentPage /> : renderRouter}
                </>
            </ContextProvider>
        </ErrorBoundary>
    );
}
